// ...existing code...
/**
 * Notification Agent
 * Manages saved searches and notifications functionality.
 */

import { useState, useCallback, useEffect } from 'react';
import APIAgent from './apiAgent';
import asArray from '../utils/asArray';

/**
 * Custom hook for managing saved searches
 * @returns {Object} Saved search state and methods
 */
export const useSavedSearches = (userId) => {
  const [searches, setSearches] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch all saved searches
   */
  const fetchSearches = useCallback(async () => {
    if (!userId) return;
    setIsLoading(true);
    setError(null);

    try {
      const response = await APIAgent.request('/api/notifications/saved-searches');
      setSearches(asArray(response));
    } catch (err) {
      console.error('Failed to fetch saved searches:', err);
      setError(err.message || 'Failed to load saved searches');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  /**
   * Create a new saved search
   * @param {string} name - Name of the saved search
   * @param {Object} criteria - Search criteria
   * @param {Object} options - Notification options
   */
  const createSearch = useCallback(async (name, criteria, options = {}) => {
    setError(null);

    try {
      const payload = {
        name,
        criteria,
        notify_on_new_listing: options.notifyNewListing !== false,
        notify_on_price_drop: options.notifyPriceDrop !== false,
        notify_frequency: options.frequency || 'immediate',
      };

      const response = await APIAgent.request(
        '/api/notifications/saved-searches',
        'POST',
        payload
      );

      setSearches((prev) => [response, ...prev]);
      return response;
    } catch (err) {
      console.error('Failed to create saved search:', err);
      setError(err.message || 'Failed to create saved search');
      throw err;
    }
  }, []);

  /**
   * Update a saved search
   * @param {number} searchId - ID of search to update
   * @param {Object} updates - Updates to apply
   */
  const updateSearch = useCallback(async (searchId, updates) => {
    setError(null);

    try {
      const response = await APIAgent.request(
        `/api/notifications/saved-searches/${searchId}`,
        'PUT',
        updates
      );

      setSearches((prev) =>
        prev.map((s) => (s.id === searchId ? response : s))
      );
      return response;
    } catch (err) {
      console.error('Failed to update saved search:', err);
      setError(err.message || 'Failed to update saved search');
      throw err;
    }
  }, []);

  /**
   * Delete a saved search
   * @param {number} searchId - ID of search to delete
   */
  const deleteSearch = useCallback(async (searchId) => {
    setError(null);

    try {
      await APIAgent.request(
        `/api/notifications/saved-searches/${searchId}`,
        'DELETE'
      );

      setSearches((prev) => prev.filter((s) => s.id !== searchId));
    } catch (err) {
      console.error('Failed to delete saved search:', err);
      setError(err.message || 'Failed to delete saved search');
      throw err;
    }
  }, []);

  /**
   * Check for new listings for a saved search
   * @param {number} searchId - ID of search to check
   */
  const checkNewListings = useCallback(async (searchId) => {
    setError(null);

    try {
      const response = await APIAgent.request(
        `/api/notifications/saved-searches/${searchId}/check-listings`,
        'POST'
      );

      return response;
    } catch (err) {
      console.error('Failed to check for new listings:', err);
      setError(err.message || 'Failed to check for new listings');
      throw err;
    }
  }, []);

  useEffect(() => {
    fetchSearches();
  }, [fetchSearches]);

  return {
    searches,
    isLoading,
    error,
    fetchSearches,
    createSearch,
    updateSearch,
    deleteSearch,
    checkNewListings,
  };
};

/**
 * Custom hook for managing user notifications
 * @returns {Object} Notification state and methods
 */
export const useNotifications = (userId) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch user notifications
   * @param {Object} options - Query options
   */
  const fetchNotifications = useCallback(async (options = {}) => {
    if (!userId) return;
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        unread_only: options.unreadOnly ? 'true' : 'false',
        limit: options.limit || 20,
        offset: options.offset || 0,
      });

      const response = await APIAgent.request(
        `/api/notifications?${params.toString()}`
      );

      // Backend contract is { notifications, unread_count }; tolerate a bare
      // array or a null/odd body instead of crashing the drawer.
      setNotifications(asArray(response?.notifications ?? response));
      setUnreadCount(Number(response?.unread_count) || 0);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      setError(err.message || 'Failed to load notifications');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  /**
   * Mark a notification as read
   * @param {number} notificationId - ID of notification to mark as read
   */
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await APIAgent.request(
        `/api/notifications/${notificationId}/read`,
        'PUT'
      );

      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, read: true } : n
        )
      );

      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
      throw err;
    }
  }, []);

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async () => {
    try {
      const promises = notifications
        .filter((n) => !n.read)
        .map((n) => markAsRead(n.id));

      await Promise.all(promises);
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
      throw err;
    }
  }, [notifications, markAsRead]);

  useEffect(() => {
    fetchNotifications();
    
    // Set up interval to poll for new notifications every 30 seconds
    const interval = setInterval(() => {
      fetchNotifications({ unreadOnly: true });
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchNotifications]);

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
  };
};
